import { FC, useMemo, useRef, useState } from 'react';
import { Button, Icon, AdminOptionList } from 'learning-panel-components';
import { translate } from 'translations';
import { getLocaleDateTimeString, mixpanelTrack, gainsightTrack } from 'utils';
import { ReportStatus, Report } from 'network-utils';
import useOuterClick from 'utils/useOuterClick';
import Loader from '@components/adminPanel/loader/loader';
import styles from './downloadHubList.module.scss';
import GreenCheck from '../utils/greenCheck';
import UndoPopup from '../utils/undoPopup';

const DownloadHubListItems: FC<DownloadHubListItemsProps> = (props: DownloadHubListItemsProps) => {
    const {
        onClose,
        rows,
        onSeeAll,
        handleDownload,
        handleRename,
        renamedSuccess,
        onRegenerate,
        handleDelete,
        handleViewed,
        undoData,
        greenCheckUuids,
        handleGreenCheckOff,
        isLoading,
        totalRows
    } = props;

    const [hoverRowUuid, setHoverRowUuid] = useState<string>('');
    const [showOptionsListRow, setShowOptionsListRow] = useState<Report | undefined>();
    const [renameRowUuid, setRenameRowUuid] = useState<string>('');
    const [renamedValue, setRenamedValue] = useState<string>('');
    const [optionsApproval, setOptionsApproval] = useState<boolean>(false);
    const [rowsRefs, setRowsRefs] = useState<any>({});

    const SEE_ALL = translate('SeeAll');
    const editText = translate('Rename');
    const deleteText = translate('Delete');
    const NEW = translate('New');

    const getOptions = (uuid: string, status: ReportStatus) => {
        return [
            {
                key: '1',
                text: editText,
                icon: 'editAdmin',
                onClick: () => {
                    setRenameRowUuid(uuid);
                    setOptionsApproval(true);
                    setRenamedValue(rows.find(row => row.uuid === uuid)?.name || '');
                    mixpanelTrack({
                        eventName: 'Download Hub Action',
                        properties: {
                            'Hub Type': 'Mini',
                            Action: 'Rename'
                        }
                    });
                    gainsightTrack('Download Hub Action', {
                        'Hub Type': 'Mini',
                        Action: 'Rename'
                    });
                }
            },
            {
                key: '2',
                text: deleteText,
                icon: 'trash',
                onClick: () => {
                    handleDelete(uuid, status);
                    mixpanelTrack({
                        eventName: 'Download Hub Action',
                        properties: {
                            'Hub Type': 'Mini',
                            Action: 'Delete'
                        }
                    });
                    gainsightTrack('Download Hub Action', {
                        'Hub Type': 'Mini',
                        Action: 'Delete'
                    });
                },
                showAnswerOptions: true
            }
        ];
    };

    const isNew = (row: Report) => {
        return !row.isViewed && row.status == ReportStatus.COMPLETED;
    };

    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    useOuterClick(ref, () => onClose());

    const onOptionsList = (row: Report) => {
        setShowOptionsListRow(row);
    };

    const setRowRef = (uuid: string, ref: HTMLDivElement | null) => {
        if (ref && !Object.keys(rowsRefs).includes(uuid)) {
            setRowsRefs({ ...rowsRefs, [uuid]: ref });
        }
    };

    const rederOptionsList = () => {
        if (showOptionsListRow && rowsRefs[showOptionsListRow.uuid]) {
            const refHook = rowsRefs[showOptionsListRow.uuid];
            const style = {
                position: 'fixed',
                top: refHook.getBoundingClientRect().y,
                bottom: 'auto',
                transform: 'none'
            };
            return (
                <AdminOptionList
                    options={getOptions(showOptionsListRow.uuid, showOptionsListRow.status)}
                    onClose={() => setShowOptionsListRow(undefined)}
                    style={style}
                />
            );
        }
        return <></>;
    };

    const sortedRows = useMemo(() => {
        return rows.sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    }, [rows]);

    return (
        <div className={styles['download-hub-list-items-wrapper']} ref={ref}>
            <div className={styles['head-wrapper']}>
                <div className={styles['title-wrapper']}>
                    <div className={styles.title}>{translate('RecentlyGeneratedReports')}</div>
                    <Icon name="close" onClick={onClose} className={styles.cancel} />
                </div>
            </div>
            {isLoading ? (
                <Loader />
            ) : (
                <div className={styles['body-wrapper']}>
                    <div className={styles.body}>
                        <div className={styles['rows-wrapper']}>
                            {sortedRows.map((row, index) => (
                                <div
                                    key={index}
                                    ref={ref => setRowRef(row.uuid, ref)}
                                    className={styles['row-wrapper']}
                                    onMouseEnter={() => setHoverRowUuid(row.uuid)}
                                    onMouseLeave={() => {
                                        setHoverRowUuid('');
                                    }}
                                >
                                    <div key={index} className={styles.left}>
                                        <div
                                            className={
                                                isNew(row) ? styles['title-wrapper-margin'] : styles['title-wrapper']
                                            }
                                        >
                                            {isNew(row) && (
                                                <div className={styles['new-wrapper']}>
                                                    <Icon name="newIcon" className={styles.icon} />
                                                    <div className={styles.text}>{NEW}</div>
                                                </div>
                                            )}
                                            {renameRowUuid === row.uuid ? (
                                                <input
                                                    autoFocus
                                                    className={styles.input}
                                                    value={renamedValue}
                                                    onChange={e => setRenamedValue(e.target.value)}
                                                ></input>
                                            ) : (
                                                <div className={!renamedSuccess ? styles.title : styles['saved-title']}>
                                                    {row.name}
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles['type-wrapper']}>
                                            <div className={styles.type}>{translate(row.source)}</div>
                                            <div className={styles.date}>
                                                {getLocaleDateTimeString(row.createdAt, 'en')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.right}>
                                        {hoverRowUuid === row.uuid && (
                                            <div className={styles['actions-wrapper']}>
                                                {row.status === ReportStatus.COMPLETED && (
                                                    <div
                                                        className={styles['action-wrapper']}
                                                        onClick={() => {
                                                            handleViewed(row.uuid);
                                                            handleDownload(row.uuid, row.source);
                                                            mixpanelTrack({
                                                                eventName: 'Download Hub Action',
                                                                properties: {
                                                                    'Hub Type': 'Mini',
                                                                    Action: 'Download'
                                                                }
                                                            });
                                                            gainsightTrack('Download Hub Action', {
                                                                'Hub Type': 'Mini',
                                                                Action: 'Download'
                                                            });
                                                        }}
                                                    >
                                                        <Icon name="DownloadAdmin" />
                                                    </div>
                                                )}
                                                {row.status === ReportStatus.FAILED_TO_DELETE && (
                                                    <div
                                                        className={styles['action-wrapper']}
                                                        onClick={() => handleDelete(row.uuid, row.status)}
                                                    >
                                                        <Icon name="retry" />
                                                    </div>
                                                )}
                                                {row.status === ReportStatus.ERROR && (
                                                    <div
                                                        className={styles['action-wrapper']}
                                                        onClick={() => {
                                                            onRegenerate(row.uuid);
                                                            mixpanelTrack({
                                                                eventName: 'Download Hub Action',
                                                                properties: {
                                                                    'Hub Type': 'Mini',
                                                                    Action: 'Retry'
                                                                }
                                                            });
                                                            gainsightTrack('Download Hub Action', {
                                                                'Hub Type': 'Mini',
                                                                Action: 'Retry'
                                                            });
                                                        }}
                                                    >
                                                        <Icon name="retry" />
                                                    </div>
                                                )}
                                                <div
                                                    className={styles['action-wrapper']}
                                                    onClick={() => {
                                                        handleViewed(row.uuid);
                                                        onOptionsList(row);
                                                    }}
                                                >
                                                    <Icon name="KebabMenuAdmin" />
                                                </div>
                                            </div>
                                        )}
                                        {renameRowUuid === row.uuid && optionsApproval && (
                                            <div className={styles['rename-wrapper']}>
                                                <Icon
                                                    name="saveAdmin"
                                                    onClick={() => {
                                                        setOptionsApproval(false);
                                                        handleRename(row.uuid, renamedValue, row.name);
                                                        setRenameRowUuid('');
                                                    }}
                                                    className={styles.icon}
                                                />
                                                <div className={styles.divider}></div>
                                                <Icon
                                                    name="close"
                                                    onClick={() => {
                                                        setOptionsApproval(false);
                                                        setRenameRowUuid('');
                                                        setRenamedValue('');
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {row.status === ReportStatus.IN_PROGRESS && (
                                            <div className={styles.loader}></div>
                                        )}
                                        {row.status === ReportStatus.ERROR && <Icon name="errorIconAdmin" />}
                                        {row.status === ReportStatus.FAILED_TO_DELETE && (
                                            <Icon name={'failedToDelete'} />
                                        )}
                                        <GreenCheck
                                            isOn={greenCheckUuids.includes(row.uuid)}
                                            handleOff={() => handleGreenCheckOff(row.uuid)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {Object.keys(undoData).length && <UndoPopup undoData={undoData} uuid={Object.keys(undoData)[0]} />}
                    {rederOptionsList()}
                </div>
            )}
            <div className={styles['footer-wrapper']}>
                <Button
                    type="learningPanelAdminActionButton"
                    text={SEE_ALL}
                    onClick={() => onSeeAll && onSeeAll(true)}
                    lowerCase={true}
                />
            </div>
        </div>
    );
};

export default DownloadHubListItems;

interface DownloadHubListItemsProps {
    onClose: Function;
    onSeeAll?: Function;
    rows: Report[];
    handleDownload: Function;
    handleRename: Function;
    renamedSuccess?: boolean;
    handleDelete: Function;
    handleViewed: Function;
    onRegenerate: Function;
    undoData: any;
    greenCheckUuids: string[];
    handleGreenCheckOff: Function;
    isLoading: boolean;
    totalRows: number;
}
